import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import { Button, Container, Paper, TextField, Typography } from '@mui/material';
import { CSSTransition } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import JWTDecode from 'jwt-decode';
import utc from 'dayjs/plugin/utc';

import { TRIGGER_WEBHOOK, useReady } from '../../shared';
import PageTitle from '../common/PageTitle';
import useInsuranceInfo from '../../hooks/useInsuranceInfo';

import {
  StyledDateInputContainer,
  StyledSecondAuthButtonContainer,
  StyledVerticalContent,
} from './Login.styled';

dayjs.extend(utc);

const SecondAuth = ({ jwt, setBirthDate, wrongDateOfBirth, setWrongDateOfBirth }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const ready = useReady();

  const dayInputRef = useRef(null);
  const monthInputRef = useRef(null);
  const yearInputRef = useRef(null);
  const submitBtnRef = useRef(null);

  const { type: insuranceType } = useInsuranceInfo();

  const decodedJwt = jwt ? JWTDecode(jwt) : {};
  const uuid = decodedJwt?.uuid;
  const email = decodedJwt?.email;

  const [triggerWebhook] = useMutation(TRIGGER_WEBHOOK, {
    variables: {
      uuid: uuid || '',
    },
  });

  useEffect(() => {
    if (!!email && !!uuid) {
      triggerWebhook();
    }
  }, [email, uuid, triggerWebhook]);

  const flushDateInput = useCallback(() => {
    setDay('');
    setMonth('');
    setYear('');
    setBirthDate('');
    setWrongDateOfBirth(false);
    if (dayInputRef) {
      dayInputRef.current.focus();
    }
  }, [setWrongDateOfBirth, setBirthDate]);

  useEffect(() => {
    if (wrongDateOfBirth) {
      setDisableForm(false);
      setIsInvalid(true);
      flushDateInput();
    }
  }, [wrongDateOfBirth, flushDateInput]);

  const onDayInputChange = (e) => {
    const val = e.target.value;
    const inputDay = val?.toString()?.slice(0, 2);

    if (inputDay?.length === 2 && monthInputRef) {
      monthInputRef.current.focus();
    }

    setDay(inputDay);
    setIsInvalid(false);
  };

  const onMonthInputChange = (e) => {
    const val = e.target.value;
    const inputMonth = val?.toString()?.slice(0, 2);

    if (inputMonth?.length === 2 && yearInputRef) {
      yearInputRef.current.focus();
    }

    setMonth(inputMonth);
    setIsInvalid(false);
  };

  const onYearInputChange = (e) => {
    const val = e.target.value;
    const inputYear = val?.toString()?.slice(0, 4);

    if (inputYear?.length === 4 && submitBtnRef) {
      submitBtnRef.current.focus();
    }

    setYear(inputYear);
    setIsInvalid(false);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    setDisableForm(true);

    const monthNum = month.length === 1 ? `0${month}` : month;
    const dayNum = day.length === 1 ? `0${day}` : day;
    const dateISOString = `${year}-${monthNum}-${dayNum}T00:00:00.000Z`;

    if (dayjs(dateISOString).isValid()) {
      setBirthDate(dateISOString);
    } else {
      setDisableForm(false);
      setIsInvalid(true);
      flushDateInput();
    }
  };

  return (
    <Container>
      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id="second.auth.title" />
        </Typography>
      </PageTitle>

      <CSSTransition in={ready} timeout={600} classNames="slow-fade" unmountOnExit>
        <Paper>
          <form onSubmit={(e) => onFormSubmit(e)}>
            <StyledVerticalContent>
              <StyledDateInputContainer>
                <TextField
                  id="second-auth-day"
                  name="second-auth-day"
                  placeholder="22"
                  label={
                    <Typography variant="h3" component="span">
                      <FormattedMessage id="second.auth.input.label.day" />
                    </Typography>
                  }
                  value={day}
                  onChange={onDayInputChange}
                  required
                  disabled={disableForm}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      ref: dayInputRef,
                    },
                  }}
                />

                <TextField
                  id="second-auth-month"
                  name="second-auth-month"
                  placeholder="10"
                  label={
                    <Typography variant="h3" component="span">
                      <FormattedMessage id="second.auth.input.label.month" />
                    </Typography>
                  }
                  value={month}
                  onChange={onMonthInputChange}
                  required
                  disabled={disableForm}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      ref: monthInputRef,
                    },
                  }}
                />

                <TextField
                  id="second-auth-year"
                  name="second-auth-year"
                  placeholder="1975"
                  label={
                    <Typography variant="h3" component="span">
                      <FormattedMessage id="second.auth.input.label.year" />
                    </Typography>
                  }
                  value={year}
                  onChange={onYearInputChange}
                  required
                  disabled={disableForm}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      ref: yearInputRef,
                    },
                  }}
                />
              </StyledDateInputContainer>

              <CSSTransition in={isInvalid} timeout={600} classNames="fade" unmountOnExit>
                <Typography variant="p" color="error">
                  <FormattedMessage id="second.auth.wrong.details" />
                </Typography>
              </CSSTransition>

              <Typography variant="p">
                <FormattedMessage id={`second.auth.description.${insuranceType}`} />
              </Typography>
            </StyledVerticalContent>

            <StyledSecondAuthButtonContainer>
              <Button ref={submitBtnRef} disabled={disableForm} type="submit">
                <Typography variant="buttons">
                  <FormattedMessage id="second.auth.login.btn" />
                </Typography>
              </Button>
            </StyledSecondAuthButtonContainer>

            <Typography variant="h3" color="product_blue" component="span">
              *&nbsp;
            </Typography>
            <Typography variant="p" component="span">
              <FormattedMessage id="second.auth.fields.required" />
            </Typography>
          </form>
        </Paper>
      </CSSTransition>
    </Container>
  );
};

SecondAuth.propTypes = {
  jwt: PropTypes.string,
  setBirthDate: PropTypes.func,
  wrongDateOfBirth: PropTypes.bool,
  setWrongDateOfBirth: PropTypes.func,
};

export default withRouter(SecondAuth);
